// Sidebar navigation config — the single source of truth for which modules
// exist, their routes / sub-menus / icons, and which users see them. Used by
// the desktop sidebar (AppLayout) and the mobile shell (src/mobile/).
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Crown, Package, Workflow, ClipboardList, ShieldCheck, FlaskConical, Factory, ShoppingCart, Truck, Users as UsersIcon, FileText, ReceiptText, Gauge, PackageOpen, PackageCheck, PackageSearch, UserCog, Briefcase, Settings, GitBranch, ScrollText, ClipboardCheck, FilePlus, Home, MapPin, Receipt, ListTodo, BarChart3, User, ShoppingBag, FileCheck2, CalendarClock, Table2, Ruler, Wrench, AlertTriangle, ClipboardSignature, RotateCcw, ListChecks, FileBarChart, Boxes, Building2, Handshake, PenTool, Database, Target, IdCard, Route,
} from "lucide-react";

// Role → allowed nav paths. `admin` sees everything. Anything not in the map
// falls through to legacy behaviour (visible to all logged-in users).
export const ROLE_MENU = {
  production_manager: new Set(["/", "/design", "/otd-board", "/routing", "/capacity", "/mrp", "/auto-procurement", "/stock-availability", "/work-orders", "/production-flow", "/production-approvals", "/quality/incoming-inspection", "/quality/production-ncrs", "/quality/rework-orders", "/indents", "/reports", "/supplier-master"]),
  quality_inspector:  new Set(["/", "/design", "/quality", "/quality-inspections", "/quality/incoming-inspection", "/quality/calibration", "/quality/production-ncrs", "/quality/rework-orders", "/goods-inward", "/goods-receipt", "/supplier-master"]),
  // Inventory now lives inside Production Flow (see PRODUCTION_FLOW_NAV
  // below) — store_manager gets the whole module so its "Inventory" item
  // is reachable, same as before the merge.
  store_manager:      new Set(["/", "/design", "/production-flow", "/stock-ledger", "/stock-availability", "/purchase-requests", "/goods-inward", "/goods-receipt", "/indents", "/reports", "/supplier-master"]),
  sales_crm:          new Set(["/", "/design", "/customers", "/dealer-master", "/crm", "/quotations", "/raise-transfer-order", "/sales-orders", "/stock-availability", "/order-status", "/order-status-sheet", "/customer-sales-orders", "/proforma-invoices", "/packing-slips", "/delivery-challans", "/tax-invoices", "/reports"]),
  marketing:          new Set(["/", "/design", "/customers", "/dealer-master", "/quotations", "/raise-transfer-order", "/sales-orders", "/stock-availability", "/order-status", "/order-status-sheet", "/customer-sales-orders", "/proforma-invoices"]),
  // Supplier / Dealer Management functional roles (backend/partners/config.py)
  purchase_manager:   new Set(["/", "/design", "/supplier-master", "/suppliers", "/mrp", "/stock-availability", "/purchase-requests", "/purchase-orders", "/auto-procurement", "/goods-inward", "/goods-receipt", "/indents", "/reports"]),
  purchase_executive: new Set(["/", "/design", "/supplier-master", "/suppliers", "/stock-availability", "/purchase-requests", "/purchase-orders", "/auto-procurement", "/goods-inward", "/goods-receipt", "/indents"]),
  sales_manager:      new Set(["/", "/design", "/dealer-master", "/customers", "/crm", "/quotations", "/raise-transfer-order", "/sales-orders", "/stock-availability", "/order-status", "/order-status-sheet", "/customer-sales-orders", "/proforma-invoices", "/packing-slips", "/delivery-challans", "/tax-invoices", "/reports"]),
  sales_executive:    new Set(["/", "/design", "/dealer-master", "/customers", "/crm", "/quotations", "/raise-transfer-order", "/sales-orders", "/stock-availability", "/order-status", "/customer-sales-orders", "/proforma-invoices"]),
  accounts:           new Set(["/", "/supplier-master", "/dealer-master", "/purchase-orders", "/goods-receipt", "/tax-invoices", "/delivery-challans"]),
  finance_manager:    new Set(["/", "/supplier-master", "/dealer-master", "/purchase-orders", "/goods-receipt", "/tax-invoices", "/delivery-challans", "/reports"]),
  // Design & Engineering roles (backend/design_engineering/config.py).
  design_engineer:    new Set(["/", "/design", "/items", "/routing", "/stock-availability"]),
  design_checker:     new Set(["/", "/design", "/items", "/routing", "/stock-availability"]),
  design_head:        new Set(["/", "/design", "/items", "/routing", "/stock-availability", "/reports"]),
  management:         new Set(["/", "/ceo-dashboard", "/otd-board", "/design", "/supplier-master", "/dealer-master", "/mrp", "/stock-availability", "/purchase-orders", "/auto-procurement", "/sales-orders", "/customer-sales-orders", "/order-status", "/tax-invoices", "/reports"]),
};

// Module key (Phase 20 permissions matrix) → nav path.  Must match backend MODULE_CATALOG.
export const MODULE_TO_PATH = {
  dashboard: "/",
  ceo_dashboard: "/ceo-dashboard",
  items_bom: "/items",
  routing: "/routing",
  capacity: "/capacity",
  mrp: "/mrp",
  work_orders: "/work-orders",
  production_flow: "/production-flow",
  production_approvals: "/production-approvals",
  otd_board: "/otd-board",
  quality: "/quality",
  qc_inspections: "/quality-inspections",
  incoming_inspection: "/quality/incoming-inspection",
  calibration: "/quality/calibration",
  production_ncrs: "/quality/production-ncrs",
  production_rework_orders: "/quality/rework-orders",
  // Inventory now lives inside Production Flow — a user with just the
  // "inventory" module permission gets the whole module so their
  // "Inventory" item under it is reachable, same as before the merge.
  inventory: "/production-flow",
  indents: "/indents",
  stock_ledger: "/stock-ledger",
  stock_availability: "/stock-availability",
  purchase_requests: "/purchase-requests",
  purchase_orders: "/purchase-orders",
  auto_procurement: "/auto-procurement",
  suppliers: "/suppliers",
  supplier_master: "/supplier-master",
  goods_inward: "/goods-inward",
  goods_receipt: "/goods-receipt",
  customers: "/customers",
  dealer_master: "/dealer-master",
  crm: "/crm",
  quotations: "/quotations",
  quotations_new: "/raise-quotation",
  raise_transfer_order: "/raise-transfer-order",
  transfer_orders: "/sales-orders",
  order_status: "/order-status",
  order_status_sheet: "/order-status-sheet",
  customer_sales_orders: "/customer-sales-orders",
  proforma_invoices: "/proforma-invoices",
  packing_slips: "/packing-slips",
  delivery_challans: "/delivery-challans",
  tax_invoices: "/tax-invoices",
  // Reports group — every report lives under /reports/*, so granting any
  // report module makes the "Reports" item (and its submenu) reachable.
  dc_vs_gin_report: "/reports",
  pending_qc_report: "/reports",
  pending_so_report: "/reports",
  pending_production_report: "/reports",
  // Sales Report sits outside NAV (its own sidebar section below), so it is
  // gated by `canSeeSalesReport` rather than this path allow-list.
  sales_report: "/sales-report",
  design_engineering: "/design",
  // Tally Data sits in the Administration section; a non-admin granted this
  // module sees just that one item there (read-only — settings stay admin).
  tally_data: "/tally",
  // HR (HR app + Logbook) — gated by `canSeeHr` like Sales Report: admins,
  // role-default users, or the "hr" module ticked on User Permissions.
  hr: "/hr",
  hr_attendance_admin: "/hr",       // HR → Attendance Admin (backend/hr_attendance.py)
  // Daily Reports sits outside NAV (its own sidebar link at the bottom), so
  // it is gated by `canSeeDailyReports` rather than this path allow-list.
  daily_reports: "/daily-reports",
};
// Sidebar sub-menu entries that have their own column on User Permissions
// (backend SUBMODULE_PARENT mirrors this): key → [parent module key, path].
// Ticking one grants its parent module; once any sub-entry of a module is
// ticked, only the ticked sub-entries are shown (otherwise all of them).
export const SUBMODULES = {
  design_dashboard: ["design_engineering", "/design"],
  design_approvals: ["design_engineering", "/design/approvals"],
  design_requests: ["design_engineering", "/design/requests"],
  design_items: ["design_engineering", "/design/items"],
  design_documents: ["design_engineering", "/design/documents"],
  design_boms: ["design_engineering", "/design/boms"],
  design_routings: ["design_engineering", "/design/routings"],
  design_changes: ["design_engineering", "/design/changes"],
  design_standards: ["design_engineering", "/design/standards"],
  design_npd: ["design_engineering", "/design/npd"],
  design_reports: ["design_engineering", "/design/reports"],
  design_settings: ["design_engineering", "/design/settings"],
  pf_inventory: ["production_flow", "/production-flow/inventory"],
  pf_raw_material: ["production_flow", "/production-flow/raw_material"],
  pf_machine_shop: ["production_flow", "/production-flow/machine_shop"],
  pf_impregnation: ["production_flow", "/production-flow/impregnation"],
  pf_fabrication: ["production_flow", "/production-flow/fabrication"],
  pf_powder_coating: ["production_flow", "/production-flow/powder_coating"],
  pf_semi_finished: ["production_flow", "/production-flow/semi_finished"],
  pf_assembly: ["production_flow", "/production-flow/assembly"],
  pf_testing: ["production_flow", "/production-flow/testing"],
  pf_final_inspection: ["production_flow", "/production-flow/final_inspection"],
  pf_finished: ["production_flow", "/production-flow/finished"],
  incoming_inspection_new: ["incoming_inspection", "/quality/incoming-inspection"],
  incoming_inspection_ncr: ["incoming_inspection", "/quality/incoming-inspection/ncr-register"],
  incoming_inspection_history: ["incoming_inspection", "/quality/incoming-inspection/history"],
  crm_all_leads: ["crm", "/crm"],
  crm_indiamart: ["crm", "/crm/indiamart"],
};
// The sub-entry key a path belongs to (longest matching sub-entry path).
export function submoduleFor(path) {
  let best = null;
  Object.entries(SUBMODULES).forEach(([k, [, p]]) => {
    if ((path === p || path.startsWith(p + "/")) && (!best || p.length > SUBMODULES[best][1].length)) best = k;
  });
  return best;
}
// Whether explicit module permissions `perms` allow sub-entry path `path`
// (paths outside any sub-menu are always allowed here).
export function subPathAllowed(perms, path) {
  if (!perms || perms.length === 0) return true;
  const key = submoduleFor(path);
  if (!key) return true;
  const parent = SUBMODULES[key][0];
  const anyTicked = perms.some((k) => SUBMODULES[k]?.[0] === parent);
  return !anyTicked || perms.includes(key);
}
// Paths shown to every user regardless of role/module permissions. Empty on
// purpose: the OTD Command Board used to be listed here, which made the
// "Production & Planning" department appear for everyone. It is now the
// "otd_board" module on User Permissions like every other module.
export const ALWAYS_VISIBLE_PATHS = new Set([]);
// Roles that own a department on Daily Reports (backend/daily_reports.py
// DEPARTMENTS) — they keep the link by role default (no explicit module
// permissions). Everyone else needs the "daily_reports" module ticked.
export const DAILY_REPORT_ROLES = new Set(["production_manager", "planner", "operator", "quality_inspector", "quality", "store_manager", "stores", "routing", "sales_crm", "marketing"]);
// Admin-only paths never appear in the module catalog — always kept for admin.
export const ADMIN_ONLY_PATHS = new Set(["/users", "/user-permissions", "/settings", "/tally"]);

export const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, tid: "nav-dashboard" },
  { to: "/ceo-dashboard", label: "CEO Dashboard", icon: Crown, tid: "nav-ceo-dashboard" },
  { to: "/hr", label: "HR", icon: IdCard, tid: "nav-hr" },
  { to: "/otd-board", label: "OTD Command Board", icon: Target, tid: "nav-otd-board" },
  { to: "/items", label: "Items & BOM", icon: Package, tid: "nav-items" },
  { to: "/design", label: "Design & Engineering", icon: PenTool, tid: "nav-design" },
  { to: "/routing", label: "Routing", icon: Workflow, tid: "nav-routing" },
  { to: "/capacity", label: "Capacity & Load", icon: Gauge, tid: "nav-capacity" },
  { to: "/mrp", label: "MRP Run", icon: FlaskConical, tid: "nav-mrp" },
  { to: "/work-orders", label: "Work Orders", icon: ClipboardList, tid: "nav-work-orders" },
  { to: "/production-flow", label: "Production Flow", icon: GitBranch, tid: "nav-production-flow" },
  { to: "/production-approvals", label: "Production Approvals", icon: ClipboardCheck, tid: "nav-production-approvals" },
  { to: "/quality", label: "Quality", icon: ShieldCheck, tid: "nav-quality" },
  { to: "/quality-inspections", label: "QC Inspections", icon: ClipboardList, tid: "nav-quality-inspections" },
  { to: "/quality/incoming-inspection", label: "Incoming/In-Process Inspection", icon: Ruler, tid: "nav-incoming-inspection" },
  { to: "/quality/calibration", label: "Calibration", icon: Wrench, tid: "nav-calibration" },
  { to: "/quality/production-ncrs", label: "Production NCRs", icon: AlertTriangle, tid: "nav-production-ncrs" },
  { to: "/quality/rework-orders", label: "Rework Orders", icon: RotateCcw, tid: "nav-rework-orders" },
  { to: "/indents", label: "Indents", icon: ClipboardSignature, tid: "nav-indents" },
  { to: "/stock-ledger", label: "Stock Ledger", icon: ScrollText, tid: "nav-stock-ledger" },
  { to: "/stock-availability", label: "Stock Availability", icon: Boxes, tid: "nav-stock-availability" },
  { to: "/purchase-requests", label: "Purchase Requests", icon: ShoppingCart, tid: "nav-purchase-requests" },
  { to: "/purchase-orders", label: "Purchase Orders", icon: ReceiptText, tid: "nav-purchase-orders" },
  { to: "/auto-procurement", label: "Auto Procurement (MRP)", icon: CalendarClock, tid: "nav-auto-procurement" },
  { to: "/supplier-master", label: "Supplier Management", icon: Building2, tid: "nav-supplier-master" },
  { to: "/suppliers", label: "Suppliers", icon: Factory, tid: "nav-suppliers" },
  { to: "/goods-inward", label: "Goods Inward (GIN)", icon: PackageOpen, tid: "nav-goods-inward" },
  { to: "/goods-receipt", label: "Goods Receipt (GRN)", icon: PackageCheck, tid: "nav-goods-receipt" },
  { to: "/dealer-master", label: "Dealer Management", icon: Handshake, tid: "nav-dealer-master" },
  { to: "/customers", label: "Customers", icon: UsersIcon, tid: "nav-customers" },
  { to: "/crm", label: "CRM · Leads", icon: Briefcase, tid: "nav-crm" },
  { to: "/quotations", label: "Quotations", icon: FileText, tid: "nav-quotations" },
  { to: "/raise-transfer-order", label: "Raise Transfer Order", icon: FilePlus, tid: "nav-raise-transfer-order" },
  { to: "/sales-orders", label: "Transfer Orders", icon: FileText, tid: "nav-sales-orders" },
  { to: "/order-status-sheet", label: "Order Status Sheet", icon: Table2, tid: "nav-order-status-sheet" },
  { to: "/customer-sales-orders", label: "Sales Order", icon: ShoppingBag, tid: "nav-customer-sales-orders" },
  { to: "/proforma-invoices", label: "Proforma Invoices", icon: Receipt, tid: "nav-proforma-invoices" },
  { to: "/packing-slips", label: "Packing Slips", icon: PackageSearch, tid: "nav-packing-slips" },
  { to: "/delivery-challans", label: "Delivery Challans", icon: Truck, tid: "nav-delivery-challans" },
  { to: "/tax-invoices", label: "Tax Invoices", icon: FileCheck2, tid: "nav-tax-invoices" },
  { to: "/reports", label: "Reports", icon: FileBarChart, tid: "nav-reports" },
];

export const ADMIN_NAV = [
  { to: "/users", label: "Users & Roles", icon: UserCog, tid: "nav-users" },
  { to: "/user-permissions", label: "User Permissions", icon: ShieldCheck, tid: "nav-user-permissions" },
  { to: "/tally", label: "Tally Data", icon: Database, tid: "nav-tally" },
  { to: "/settings", label: "Settings", icon: Settings, tid: "nav-settings" },
];

// Departments — the sidebar groups the department modules under a
// "Departments" heading, one collapsible dropdown per department. Each entry
// re-uses an existing route; `base` (default: `to`) is the NAV / ADMIN_NAV /
// Sales Report path whose visibility gates it, so who sees what is exactly
// the same as before (a department with nothing visible is hidden). NAV
// items not placed in any department stay at the top level of the sidebar.
// Testids reuse the NAV item's tid where the entry *is* that NAV item.
export const DEPARTMENTS = [
  // Design & Engineering lists its own pages (the DESIGN_NAV submenu) —
  // Settings keeps its admin / design_head-only `roles` filter.
  { key: "design", label: "Design & Engineering", icon: PenTool, items: [
    { to: "/design", end: true, label: "Dashboard", icon: LayoutDashboard, tid: "nav-design-dashboard" },
    { to: "/design/approvals", base: "/design", label: "My Queue", icon: ListChecks, tid: "nav-design-approvals" },
    { to: "/design/requests", base: "/design", label: "Design Requests", icon: FilePlus, tid: "nav-design-requests" },
    { to: "/design/items", base: "/design", label: "Item Master", icon: Package, tid: "nav-design-items" },
    { to: "/design/documents", base: "/design", label: "Drawings & Documents", icon: FileText, tid: "nav-design-documents" },
    { to: "/design/boms", base: "/design", label: "Bills of Material", icon: Boxes, tid: "nav-design-boms" },
    { to: "/design/routings", base: "/design", label: "Routings", icon: Workflow, tid: "nav-design-routings" },
    { to: "/design/changes", base: "/design", label: "ECR / ECN", icon: GitBranch, tid: "nav-design-changes" },
    { to: "/design/standards", base: "/design", label: "Standards Library", icon: ScrollText, tid: "nav-design-standards" },
    { to: "/design/npd", base: "/design", label: "NPD Projects", icon: Target, tid: "nav-design-npd" },
    { to: "/design/reports", base: "/design", label: "Reports & Audit", icon: FileBarChart, tid: "nav-design-reports" },
    { to: "/design/settings", base: "/design", label: "Settings", icon: Settings, tid: "nav-design-settings", roles: ["admin", "design_head"] },
  ] },
  { key: "production-planning", label: "Production & Planning", icon: Factory, items: [
    { to: "/production-flow", label: "Production Flow", icon: GitBranch, tid: "nav-production-flow" },
    { to: "/quality/rework-orders", label: "Rework Orders", icon: RotateCcw, tid: "nav-rework-orders" },
    { to: "/otd-board", label: "OTD Dashboard", icon: Target, tid: "nav-otd-board" },
    { to: "/production-approvals", label: "Production Approvals", icon: ClipboardCheck, tid: "nav-production-approvals" },
    { to: "/mrp", label: "MRP Run", icon: FlaskConical, tid: "nav-mrp" },
    { to: "/capacity", label: "Capacity & Load", icon: Gauge, tid: "nav-capacity" },
    { to: "/work-orders", label: "Work Orders", icon: ClipboardList, tid: "nav-work-orders" },
    { to: "/quality/production-ncrs", label: "Production NCRs", icon: AlertTriangle, tid: "nav-production-ncrs" },
    { to: "/indents", label: "Indents", icon: ClipboardSignature, tid: "nav-indents" },
    { to: "/reports/pending-production", base: "/reports", label: "Pending Production Orders", icon: FileBarChart, tid: "nav-dept-production-pending-production" },
  ] },
  { key: "quality", label: "Quality", icon: ShieldCheck, items: [
    { to: "/quality", end: true, label: "Quality Inspections", icon: ShieldCheck, tid: "nav-quality" },
    { to: "/quality/incoming-inspection", label: "Incoming/In-Process Inspection", icon: Ruler, tid: "nav-incoming-inspection" },
    { to: "/quality-inspections", label: "QC Inspections", icon: ClipboardList, tid: "nav-quality-inspections" },
    { to: "/quality/incoming-inspection/ncr-register", base: "/quality/incoming-inspection", label: "NCR", icon: AlertTriangle, tid: "nav-dept-quality-ncr" },
    { to: "/quality/incoming-inspection/history", base: "/quality/incoming-inspection", label: "Report History", icon: ScrollText, tid: "nav-dept-quality-report-history" },
    { to: "/quality/calibration", label: "Calibration", icon: Wrench, tid: "nav-calibration" },
    { to: "/reports/pending-qc", base: "/reports", label: "Pending QC Report", icon: FileBarChart, tid: "nav-dept-quality-pending-qc" },
    { to: "/goods-inward", label: "GIN", icon: PackageOpen, tid: "nav-goods-inward" },
    { to: "/goods-receipt", label: "GRN", icon: PackageCheck, tid: "nav-goods-receipt" },
  ] },
  { key: "marketing", label: "Marketing", icon: Briefcase, items: [
    { to: "/raise-transfer-order", label: "Raise Transfer Order", icon: FilePlus, tid: "nav-raise-transfer-order" },
    { to: "/sales-orders", label: "Transfer Orders", icon: FileText, tid: "nav-sales-orders" },
    { to: "/order-status-sheet", label: "Transfer Order Sheet", icon: Table2, tid: "nav-order-status-sheet" },
    { to: "/customers", label: "Customers", icon: UsersIcon, tid: "nav-customers" },
    { to: "/dealer-master", label: "Dealer Management", icon: Handshake, tid: "nav-dealer-master" },
    { to: "/crm", label: "CRM · Leads", icon: Briefcase, tid: "nav-crm" },
    { to: "/quotations", label: "Quotations", icon: FileText, tid: "nav-quotations" },
    { to: "/sales-report", label: "Sales Report", icon: Route, tid: "nav-sales-report" },
  ] },
  { key: "sales", label: "Sales", icon: ShoppingBag, items: [
    { to: "/customer-sales-orders", label: "Sales Order", icon: ShoppingBag, tid: "nav-customer-sales-orders" },
    { to: "/reports/pending-sales-orders", base: "/reports", label: "Pending Sales Order Report", icon: FileBarChart, tid: "nav-dept-sales-pending-so" },
    { to: "/proforma-invoices", label: "Proforma Invoices", icon: Receipt, tid: "nav-proforma-invoices" },
    { to: "/tax-invoices", label: "Tax Invoices", icon: FileCheck2, tid: "nav-tax-invoices" },
  ] },
  { key: "inventory", label: "Inventory Management", icon: Boxes, items: [
    { to: "/items", label: "Items & BOM", icon: Package, tid: "nav-items" },
    { to: "/routing", label: "Routing", icon: Workflow, tid: "nav-routing" },
    { to: "/stock-ledger", label: "Stock Ledger", icon: ScrollText, tid: "nav-stock-ledger" },
    { to: "/stock-availability", label: "Stock Availability", icon: Boxes, tid: "nav-stock-availability" },
    { to: "/reports/dc-vs-gin", base: "/reports", label: "DC vs GIN Report", icon: FileBarChart, tid: "nav-dept-inventory-dc-vs-gin" },
  ] },
  { key: "purchase", label: "Purchase", icon: ShoppingCart, items: [
    { to: "/purchase-requests", label: "Purchase Requests", icon: ShoppingCart, tid: "nav-purchase-requests" },
    { to: "/purchase-orders", label: "Purchase Orders", icon: ReceiptText, tid: "nav-purchase-orders" },
    { to: "/auto-procurement", label: "Auto Procurement (MRP)", icon: CalendarClock, tid: "nav-auto-procurement" },
    { to: "/suppliers", label: "Suppliers", icon: Factory, tid: "nav-suppliers" },
    { to: "/supplier-master", label: "Supplier Management", icon: Building2, tid: "nav-supplier-master" },
  ] },
  { key: "dispatch", label: "Dispatch", icon: Truck, items: [
    { to: "/packing-slips", label: "Packing Slips", icon: PackageSearch, tid: "nav-packing-slips" },
    { to: "/delivery-challans", label: "Delivery Challans", icon: Truck, tid: "nav-delivery-challans" },
  ] },
  { key: "admin", label: "Admin", icon: UserCog, items: [
    { to: "/users", label: "Users & Roles", icon: UserCog, tid: "nav-users" },
    { to: "/user-permissions", label: "User Permissions", icon: ShieldCheck, tid: "nav-user-permissions" },
    { to: "/settings", label: "Settings", icon: Settings, tid: "nav-settings" },
  ] },
  { key: "accounts", label: "Accounts", icon: Database, items: [
    { to: "/tally", label: "Tally Data", icon: Database, tid: "nav-tally" },
  ] },
];
// Routes placed in a department, hidden from the top level. Not their `base`
// gates in general — but Reports is listed explicitly: every report in its
// submenu (REPORTS_NAV) now sits in a department, so the top-level Reports
// menu would only repeat them.
export const DEPARTMENT_PATHS = new Set([...DEPARTMENTS.flatMap((d) => d.items.map((i) => i.to)), "/reports"]);

// Production Flow submenu — each stage opens a dedicated single-stage page
// (Issue / Receive / Create Job Work DC actions live there). Order follows
// the actual process flow; Finished Goods is last and has no DC action.
// Raw Material isn't a job stage (no job exists yet) — it lists RM Store
// stock instead, with Issue + Create Job Work DC only, no Receive.
// Inventory (first) replaces the old standalone Inventory module — every
// store's stock in one table (RM Store through Finished Goods), not just
// the 9 production stages. The multi-column "All Stages" Kanban overview
// (still reachable at /production-flow directly) was dropped from this
// submenu by request — operators work stage-by-stage, not from an overview.
export const PRODUCTION_FLOW_NAV = [
  { to: "/production-flow/inventory", label: "Inventory", tid: "nav-production-flow-inventory" },
  { to: "/production-flow/raw_material", label: "Raw Material", tid: "nav-production-flow-raw_material" },
  { to: "/production-flow/machine_shop", label: "Machine Shop", tid: "nav-production-flow-machine_shop" },
  { to: "/production-flow/impregnation", label: "Impregnation", tid: "nav-production-flow-impregnation" },
  { to: "/production-flow/fabrication", label: "Fabrication", tid: "nav-production-flow-fabrication" },
  { to: "/production-flow/powder_coating", label: "Powder Coating", tid: "nav-production-flow-powder_coating" },
  { to: "/production-flow/semi_finished", label: "Semi Finished", tid: "nav-production-flow-semi_finished" },
  { to: "/production-flow/assembly", label: "Assembly", tid: "nav-production-flow-assembly" },
  { to: "/production-flow/testing", label: "Testing", tid: "nav-production-flow-testing" },
  { to: "/production-flow/final_inspection", label: "Final Inspection", tid: "nav-production-flow-final_inspection" },
  { to: "/production-flow/finished", label: "Finished Goods", tid: "nav-production-flow-finished" },
];

// Incoming/In-Process Inspection submenu — merged in from the standalone
// SPC/NCR tool. New Inspection is the "/quality/incoming-inspection" toggle
// item itself; these three links only need to reach its subroutes.
export const INCOMING_INSPECTION_NAV = [
  { to: "/quality/incoming-inspection", label: "New Inspection", tid: "nav-incoming-inspection-new" },
  { to: "/quality/incoming-inspection/history", label: "Report History", tid: "nav-incoming-inspection-history" },
  { to: "/quality/incoming-inspection/ncr-register", label: "NCR Register", tid: "nav-incoming-inspection-ncr-register" },
];

// Design & Engineering submenu (PLM-lite module — backend/design_engineering/).
// Settings is filtered to admin / design_head in the render below; every
// other page checks the user's design permissions itself.
export const DESIGN_NAV = [
  { to: "/design", label: "Dashboard", tid: "nav-design-dashboard" },
  { to: "/design/approvals", label: "My Queue", tid: "nav-design-approvals" },
  { to: "/design/requests", label: "Design Requests", tid: "nav-design-requests" },
  { to: "/design/items", label: "Item Master", tid: "nav-design-items" },
  { to: "/design/documents", label: "Drawings & Documents", tid: "nav-design-documents" },
  { to: "/design/boms", label: "Bills of Material", tid: "nav-design-boms" },
  { to: "/design/routings", label: "Routings", tid: "nav-design-routings" },
  { to: "/design/changes", label: "ECR / ECN", tid: "nav-design-changes" },
  { to: "/design/standards", label: "Standards Library", tid: "nav-design-standards" },
  { to: "/design/npd", label: "NPD Projects", tid: "nav-design-npd" },
  { to: "/design/reports", label: "Reports & Audit", tid: "nav-design-reports" },
  { to: "/design/settings", label: "Settings", tid: "nav-design-settings", roles: ["admin", "design_head"] },
];

// Tabs across the top of the HR pages (pages/hr/HrTabs.jsx) — the Quickair
// HR app, plus Logbook (pages/hr/Logbook.jsx, backend/hr_logbook.py): every
// Logbook screen except Time Tracking. The sidebar shows HR as one item.
export const HR_NAV = [
  { to: "/hr", label: "HR App", tid: "nav-hr-app" },
  { to: "/hr/attendance", label: "Attendance", tid: "nav-hr-attendance" },
  { to: "/hr/attendance/admin", label: "Attendance Admin", tid: "nav-hr-attendance-admin" },
  { to: "/hr/logbook/mine", label: "My Tasks", tid: "nav-hr-logbook-mine" },
  { to: "/hr/logbook/given", label: "Assigned by Me", tid: "nav-hr-logbook-given" },
  { to: "/hr/logbook/team", label: "Team Board", tid: "nav-hr-logbook-team" },
  { to: "/hr/logbook/assign", label: "Assign Task", tid: "nav-hr-logbook-assign" },
  { to: "/hr/logbook/admin", label: "Logbook Admin", tid: "nav-hr-logbook-admin" },
];

// Reports submenu — cross-module, read-only reports.
export const REPORTS_NAV = [
  { to: "/reports/dc-vs-gin", label: "DC vs GIN Report", tid: "nav-reports-dc-vs-gin" },
  { to: "/reports/pending-qc", label: "Pending QC Report", tid: "nav-reports-pending-qc" },
  { to: "/reports/pending-sales-orders", label: "Pending Sales Order Report", tid: "nav-reports-pending-so" },
  { to: "/reports/pending-production", label: "Pending Production Orders", tid: "nav-reports-pending-production" },
];

// CRM submenu — "All Leads" is the existing manual/mixed-source pipeline
// (still at /crm itself); "IndiaMART Leads" is the new Pull-API-fed list.
export const CRM_NAV = [
  { to: "/crm", label: "All Leads", tid: "nav-crm-all" },
  { to: "/crm/indiamart", label: "IndiaMART Leads", tid: "nav-crm-indiamart" },
];

// Sales Report module (Phase 6 — sidebar wiring only, no new backend logic).
// Shown to admins and to users without explicit module_permissions (role
// default). A user with explicit module_permissions sees it only when the
// "sales_report" module is ticked on User Permissions. A user who hasn't
// opted into the module yet still gets a friendly message on Home (see
// SalesHome.jsx).
export const SALES_REPORT_NAV = [
  { to: "/sales-report/home", label: "Home", icon: Home, tid: "nav-sales-report-home" },
  { to: "/sales-report/visits", label: "Visits", icon: MapPin, tid: "nav-sales-report-visits" },
  { to: "/sales-report/expenses", label: "Expenses", icon: Receipt, tid: "nav-sales-report-expenses" },
  { to: "/sales-report/plans", label: "Plans", icon: ListTodo, tid: "nav-sales-report-plans" },
  { to: "/sales-report/tasks", label: "Tasks", icon: ListChecks, tid: "nav-sales-report-tasks" },
  { to: "/sales-report/reports", label: "Reports", icon: BarChart3, tid: "nav-sales-report-reports" },
  { to: "/sales-report/profile", label: "Profile", icon: User, tid: "nav-sales-report-profile" },
];

// Per-user restricted views — an account listed here sees exactly these
// sidebar entries, whatever its role or module permissions say (admins
// included), and is bounced to `home` from any other URL. Keyed by
// lower-cased email; the backend mirror (backend/user_view_restrictions.py)
// 403s every other API for the same account. Everyone else is unaffected.
//   general     — top-level items (NAV paths); `links` points one at a
//                 different page (HR opens Attendance Admin)
//   hrTabs      — the HR tabs (HR_NAV paths) the account sees
//   departments — [department key, [item paths]] in sidebar order; the
//                 items come from DEPARTMENTS, `labels` renames one.
//   routes      — URL prefixes the account may open (pages behind the items).
//   fallbacks   — where a blocked URL under a prefix goes instead of `home`.
export const USER_VIEW_RESTRICTIONS = {
  "gajanan@canares.com": {
    home: "/ceo-dashboard",
    general: ["/ceo-dashboard", "/hr"],
    links: { "/hr": "/hr/attendance/admin" },
    // HR without the HR App, Attendance (own check-in) and My Tasks.
    hrTabs: ["/hr/attendance/admin", "/hr/logbook/given", "/hr/logbook/team", "/hr/logbook/assign", "/hr/logbook/admin"],
    departments: [
      ["quality", ["/quality/incoming-inspection/ncr-register", "/reports/pending-qc"]],
      ["sales", ["/reports/pending-sales-orders"]],
      ["dispatch", ["/delivery-challans"]],
      ["marketing", ["/sales-report"]],
    ],
    labels: { "/delivery-challans": "Pending DC" },
    routes: [
      "/ceo-dashboard",
      "/hr/attendance/admin",
      "/hr/logbook/given",
      "/hr/logbook/team",
      "/hr/logbook/assign",
      "/hr/logbook/admin",
      "/quality/incoming-inspection/ncr-register",
      "/quality/incoming-inspection/ncr",       // an NCR opened from the register
      "/reports/pending-qc",
      "/reports/pending-sales-orders",
      "/delivery-challans",
      "/sales-report",
    ],
    fallbacks: { "/hr": "/hr/attendance/admin", "/hr/logbook": "/hr/logbook/given" },
  },
};
export const userViewRestriction = (user) => USER_VIEW_RESTRICTIONS[(user?.email || "").trim().toLowerCase()] || null;

// The sidebar a restricted account sees (see USER_VIEW_RESTRICTIONS).
function restrictedNav(r) {
  const deptItems = DEPARTMENTS.flatMap((d) => d.items);
  const item = (to) => {
    const found = deptItems.find((i) => i.to === to);
    return found && { ...found, label: r.labels?.[to] || found.label };
  };
  const visibleGeneralNav = NAV.filter((n) => r.general.includes(n.to)).map((n) => (r.links?.[n.to] ? { ...n, to: r.links[n.to] } : n));
  const visibleDepartments = r.departments
    .map(([key, paths]) => {
      const d = DEPARTMENTS.find((x) => x.key === key);
      return d && { ...d, items: paths.map(item).filter(Boolean) };
    })
    .filter((d) => d && d.items.length > 0);
  return { visibleGeneralNav, visibleDepartments };
}
const underPath = (path, p) => path === p || path.startsWith(p + "/");
const restrictedRouteAllowed = (r, path) => r.routes.some((p) => underPath(path, p));
const restrictedFallback = (r, path) => Object.entries(r.fallbacks || {})
  .filter(([p]) => underPath(path, p)).sort((a, b) => b[0].length - a[0].length)[0]?.[1] || r.home;

// Which nav entries `user` sees, exactly as the desktop sidebar has always
// computed it, and bounces the user to Dashboard from a URL outside their
// allow-list. Returns the pieces both layouts render from. The mobile shell
// passes `{ mobileShell: true }`: its own screens live at "/?m=…", which a
// restricted account (USER_VIEW_RESTRICTIONS) may open — except the
// Dashboard ("/?m=dashboard").
export function useVisibleNav(user, { mobileShell = false } = {}) {
  const nav = useNavigate();
  const location = useLocation();

  // Priority order:
  //  1. Admin → sees everything
  //  2. Explicit per-user module_permissions (Phase 20) → filter NAV to only those paths
  //  3. Legacy role-menu allow-list (backward compat)
  //  4. No restriction (legacy fine-grained roles)
  const perms = user?.module_permissions || [];
  let allowedPaths = null;
  if (user?.role === "admin") {
    allowedPaths = null;                                     // full access
  } else if (perms.length > 0) {
    // A ticked sub-entry grants its parent module's path.
    const paths = new Set(perms.map((k) => MODULE_TO_PATH[SUBMODULES[k]?.[0] || k]).filter(Boolean));
    if (perms.includes("quotations")) paths.add("/raise-quotation");
    if (!paths.has("/")) paths.add("/");                     // Dashboard always reachable
    allowedPaths = paths;
  } else if (ROLE_MENU[user?.role]) {
    allowedPaths = ROLE_MENU[user.role];
  }

  // Sales Report: admin and role-default users (no explicit module
  // permissions) keep seeing it; explicit permissions must include it.
  const canSeeSalesReport = user?.role === "admin" || perms.length === 0 || perms.includes("sales_report");
  // HR: same rule, with the "hr" module (backend: hr_bridge.can_use_hr).
  const canSeeHr = user?.role === "admin" || perms.length === 0 || perms.includes("hr") || perms.includes("hr_attendance_admin");
  // CEO Dashboard (backend/ceo_dashboard.py): admin, the management role, or
  // the "ceo_dashboard" module ticked on User Permissions — never by default.
  const canSeeCeo = user?.role === "admin" || user?.role === "management" || perms.includes("ceo_dashboard");
  // Daily Reports: admin, the "daily_reports" module ticked, or (without
  // explicit permissions) a role that owns a Daily Reports department.
  const canSeeDailyReports = user?.role === "admin" || perms.includes("daily_reports")
    || (perms.length === 0 && DAILY_REPORT_ROLES.has(user?.role));
  // (CRM's sidebar entry is a sub-menu toggle, so it stays visible itself.)
  // Sub-menu entries (Design pages, Production Flow stages, Incoming
  // Inspection pages, CRM lists) the user's ticked sub-entries allow.
  const subAllowed = (path) => user?.role === "admin" || subPathAllowed(perms, path);

  const visibleAdminNav = user?.role === "admin"
    ? ADMIN_NAV
    : ADMIN_NAV.filter((n) => n.to === "/tally" && perms.includes("tally_data"));

  // HR follows `canSeeHr` rather than the path allow-list (role-scoped
  // users without explicit permissions keep it, as before).
  const visibleNav = NAV.filter((n) => (n.to === "/hr" ? canSeeHr : n.to === "/ceo-dashboard" ? canSeeCeo : !allowedPaths
    || allowedPaths.has(n.to) || ALWAYS_VISIBLE_PATHS.has(n.to) || (user?.role === "admin" && ADMIN_ONLY_PATHS.has(n.to))));

  // If a scoped user hits a URL not in their allow-list, bounce to Dashboard.
  // Sales Report is gated by `canSeeSalesReport` instead of the path
  // allow-list (role-scoped users without explicit permissions keep it);
  // access within the module (opted-in or not) is handled by its own
  // pages/backend.
  const restriction = userViewRestriction(user);
  useEffect(() => {
    const path = location.pathname;
    if (restriction) {
      const shellScreen = mobileShell && path === "/" && new URLSearchParams(location.search).get("m") !== "dashboard";
      if (!shellScreen && !restrictedRouteAllowed(restriction, path)) nav(restrictedFallback(restriction, path), { replace: true });
      return;
    }
    if (path.startsWith("/sales-report")) {
      if (!canSeeSalesReport) nav("/", { replace: true });
      return;
    }
    if (path === "/ceo-dashboard" || path.startsWith("/ceo-dashboard/")) {
      if (!canSeeCeo) nav("/", { replace: true });
      return;
    }
    if (path === "/hr" || path.startsWith("/hr/")) {
      if (!canSeeHr) nav("/", { replace: true });
      return;
    }
    if (path === "/daily-reports" || path.startsWith("/daily-reports/")) {
      if (!canSeeDailyReports) nav("/", { replace: true });
      return;
    }
    if (!allowedPaths) return;
    if ([...ALWAYS_VISIBLE_PATHS].some((p) => path === p || path.startsWith(p + "/"))) return;
    // Match by prefix so nested routes still work if we add them later
    const allowed = [...allowedPaths].some((p) => p === "/" ? path === "/" : path === p || path.startsWith(p + "/"));
    if (!allowed || !subAllowed(path)) nav("/", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, allowedPaths, canSeeSalesReport, canSeeHr, canSeeCeo, canSeeDailyReports, restriction, mobileShell, nav]);

  // Departments: an item is shown when the module that gates it is visible
  // (same rules as above); a department with no visible items is hidden.
  const visiblePaths = new Set([
    ...visibleNav.map((n) => n.to),
    ...visibleAdminNav.map((n) => n.to),
    ...(canSeeSalesReport ? ["/sales-report"] : []),
  ]);
  const visibleDepartments = DEPARTMENTS
    .map((d) => ({ ...d, items: d.items.filter((i) => visiblePaths.has(i.base || i.to) && (i.to === "/crm" || subAllowed(i.to)) && (!i.roles || i.roles.includes(user?.role))) }))
    .filter((d) => d.items.length > 0);
  // Top-level items are whatever visible NAV entries no department holds
  // (Dashboard, CEO Dashboard, HR, Items & BOM, Design, Reports, ...).
  const visibleGeneralNav = visibleNav.filter((n) => !DEPARTMENT_PATHS.has(n.to));

  if (restriction) {
    const { visibleGeneralNav: rGeneral, visibleDepartments: rDepts } = restrictedNav(restriction);
    return {
      perms, allowedPaths, subAllowed: (path) => restrictedRouteAllowed(restriction, path),
      canSeeSalesReport: true, canSeeHr: restriction.general.includes("/hr"), canSeeCeo: restriction.general.includes("/ceo-dashboard"), canSeeDailyReports: false,
      visibleAdminNav: [], visibleNav: rGeneral, visibleDepartments: rDepts, visibleGeneralNav: rGeneral,
      restriction,
    };
  }
  return { perms, allowedPaths, subAllowed, canSeeSalesReport, canSeeHr, canSeeCeo, canSeeDailyReports, visibleAdminNav, visibleNav, visibleDepartments, visibleGeneralNav };
}
